import { useState } from 'react';
import { Dialog } from '../../components/Dialog.tsx';
import { SelectField } from '../../components/SelectField.tsx';
import { useAssignIssue, useCategories, useZoneUsers } from './api.ts';
import { zoneErrorMessage } from './errors.ts';
import type { IssueDetail } from './types.ts';

export function AssignDialog({ issue, open, onClose }: { issue: IssueDetail; open: boolean; onClose: () => void }): JSX.Element | null {
  const categories = useCategories();
  const staff = useZoneUsers('STAFF');
  const assign = useAssignIssue();
  const [categoryId, setCategoryId] = useState(issue.categoryId ?? '');
  const [assigneeId, setAssigneeId] = useState(issue.assigneeId ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    assign.mutate(
      { id: issue.id, categoryId, assigneeId },
      { onSuccess: onClose, onError: (err) => setError(zoneErrorMessage(err)) },
    );
  };

  const loading = categories.isLoading || staff.isLoading;
  const loadFailed = categories.isError || staff.isError;
  const title = issue.status === 'SUBMITTED' ? 'Assign issue' : 'Reassign issue';

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form onSubmit={submit} className="space-y-5">
        {loading && <p className="status-label" role="status">Loading…</p>}
        {loadFailed && <p className="text-sm text-destructive" role="alert">Could not load categories or staff. Close and try again.</p>}
        <div className="space-y-1.5">
          <label className="field-label" htmlFor="assign-category">Category</label>
          <SelectField
            id="assign-category" label="Category" placeholder="Choose a category"
            value={categoryId} onChange={setCategoryId}
            options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
        <div className="space-y-1.5">
          <label className="field-label" htmlFor="assign-staff">Assign to</label>
          <SelectField
            id="assign-staff" label="Assign to" placeholder="Choose a staff member"
            value={assigneeId} onChange={setAssigneeId}
            options={(staff.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
          />
        </div>
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={!categoryId || !assigneeId || assign.isPending}>
            {assign.isPending ? 'Saving…' : title.split(' ')[0]}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
